import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { AlertTriangle, X } from "lucide-react";

import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import IngredientsPage from "./pages/IngredientsPage";
import RecipeBuilder from "./pages/RecipeBuilder";
import LaborCalculator from "./pages/LaborCalculator";
import BreakEvenCalculator from "./pages/BreakEvenCalculator";
import SpecialsCalculator from "./pages/SpecialsCalculator";
import UploadCenter from "./pages/UploadCenter";
import ReportsPage from "./pages/ReportsPage";
import RecipeCompare from "./pages/RecipeCompare";
import GuidedWizard from "./pages/GuidedWizard";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import RegisterPage from "./pages/RegisterPage";
import UserLoginPage from "./pages/UserLoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import Sidebar from "./components/layout/Sidebar";

function SessionWarning() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('pizzalator-warning-dismissed');
    if (!dismissed) setShow(true);
  }, []);

  const dismiss = () => {
    setShow(false);
    sessionStorage.setItem('pizzalator-warning-dismissed', '1');
  };

  if (!show) return null;

  return (
    <div className="bg-[#FFC107] border-b-2 border-[#2A2A2A] px-4 py-2.5 flex items-center justify-between gap-3" data-testid="session-warning">
      <div className="flex items-center gap-2 text-[#2A2A2A] text-sm">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span style={{ fontFamily: "'Outfit', sans-serif" }}>
          <strong>Heads up!</strong> Your data lives in this browser tab. Refreshing the page will reset everything. Use <strong>Export</strong> to save your work.
        </span>
      </div>
      <button onClick={dismiss} className="text-[#2A2A2A]/60 hover:text-[#2A2A2A] flex-shrink-0" data-testid="dismiss-session-warning">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function AppLayout() {
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main lg:pl-0 pt-14 lg:pt-0">
        <SessionWarning />
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/ingredients" element={<IngredientsPage />} />
          <Route path="/recipes" element={<RecipeBuilder />} />
          <Route path="/labor" element={<LaborCalculator />} />
          <Route path="/break-even" element={<BreakEvenCalculator />} />
          <Route path="/specials" element={<SpecialsCalculator />} />
          <Route path="/upload" element={<UploadCenter />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/compare" element={<RecipeCompare />} />
          <Route path="/wizard" element={<GuidedWizard />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
      </main>
    </div>
  );
}

function AdminRoute() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Prevent search engines from indexing admin pages
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (token) {
      setAdmin({ token });
    }
    setChecking(false);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setAdmin(null);
  };

  if (checking) return null;
  if (!admin) return <AdminLogin onLogin={(data) => setAdmin(data)} />;
  return <AdminDashboard onLogout={handleLogout} />;
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/login" element={<UserLoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/*" element={<AppLayout />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
